/**
 * Tutor registration detail tab.
 * Completion bar, program, onboarding checklist, and wished establishments.
 */
import * as React from 'react';
import { BarChart3, BookOpen, Building2, CheckCircle2, FileCheck, XCircle } from 'lucide-react';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { TUTOR_INPERSON_REGISTRATION_STEPS } from '@/components/tutors/tutor-configs';
import { useEstablishment } from '@/hooks/useEstablishments';
import {
  completionBarColor,
  getTutorCoursePathLabel,
  getTutorTutoringTypeLabel,
  getTutorWishListKey,
  isTutorRegistrationStepComplete,
  mapTutorProgramChips,
  type TutorContentDisplay,
} from '@/components/tutors/inperson/tutor-table-utils';

/** Resolve an establishment id to a live name (fallback = stored label). */
const EstablishmentNameLabel: React.FC<{ id?: string; fallback?: string }> = ({ id, fallback }) => {
  const { data: estab } = useEstablishment(id ?? '');
  return <>{estab?.name ?? fallback ?? id ?? ''}</>;
};

/** Props for {@link TutorRegistrationTab}. */
interface Props {
  display: TutorContentDisplay;
}

/** Tutor registration detail tab. */
export const TutorRegistrationTab: React.FC<Props> = ({ display }) => {
  const { completionPct, sections, wishes, hasProgramSection, myEngagement } = display;

  return (
    <>
      <DetailsCard icon={BarChart3} title="Progression globale" colorName="color-3" colSpan={2}>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Complétion du dossier</span>
            <span className="text-sm font-semibold">{Math.round(completionPct)}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${completionPct}%`,
                backgroundColor: completionBarColor(completionPct),
              }}
            />
          </div>
        </div>
      </DetailsCard>

      {hasProgramSection && (
        <DetailsCard icon={BookOpen} title="Programme" colorName="color-3" colSpan={2}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {myEngagement.tutoringType && (
              <DetailField
                label="Type d'accompagnement"
                value={getTutorTutoringTypeLabel(myEngagement.tutoringType)}
              />
            )}
            {myEngagement.coursePath && (
              <DetailField
                label="Parcours"
                value={getTutorCoursePathLabel(myEngagement.coursePath)}
              />
            )}
            {Array.isArray(myEngagement.programs) && myEngagement.programs.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground detail-field-label">
                  Programme(s)
                </span>
                <DetailChipList
                  items={mapTutorProgramChips(myEngagement.programs)}
                  colorScheme="color-3"
                  variant="outlined"
                />
              </div>
            )}
          </div>
        </DetailsCard>
      )}

      <DetailsCard icon={FileCheck} title="Checklist étapes" colorName="color-2" colSpan={2}>
        <div className="space-y-2">
          {TUTOR_INPERSON_REGISTRATION_STEPS.map((step) => {
            const isComplete = isTutorRegistrationStepComplete(sections, step);
            return (
              <div key={step.key} className="flex items-center gap-2">
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4 text-[rgb(var(--status-success))]" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="text-sm">{step.label}</span>
              </div>
            );
          })}
        </div>
      </DetailsCard>

      <DetailsCard
        icon={Building2}
        title="Établissements souhaités"
        colorName="color-3"
        colSpan={2}
      >
        {wishes.length > 0 ? (
          <ul className="space-y-1">
            {wishes.map((wish) => (
              <li key={getTutorWishListKey(wish)} className="text-sm">
                •{' '}
                <EstablishmentNameLabel
                  id={wish.establishmentId}
                  fallback={wish.establishmentName}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Aucun établissement souhaité</p>
        )}
      </DetailsCard>
    </>
  );
};
